import { forwardRef } from "react";
import { motion } from "framer-motion";
import { formatNewsDate, newsCategoryLabel } from "@/content/news";
import { ExtLink } from "@/components/common/ExtLink";

const Meta = ({ n }) => (
  <div className="flex flex-wrap items-center gap-3">
    {n.date && (
      <time dateTime={n.date} className="font-mono text-[12px] uppercase tracking-[0.16em] text-muted" data-testid={`news-card-${n.id}-date`}>
        {formatNewsDate(n.date)}
      </time>
    )}
    <span className="rounded-none bg-accent-soft px-3 py-1 font-mono text-[12px] uppercase tracking-[0.14em] text-accent">{newsCategoryLabel(n.category)}</span>
  </div>
);

const Source = ({ n }) =>
  n.link ? (
    <div className="space-y-2">
      <p className="text-[13px] text-muted">Source: {n.source}</p>
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-[14px]">
        <ExtLink href={n.link} testId={`news-card-${n.id}-link`}>Read the full story</ExtLink>
        {n.secondary && (
          <ExtLink href={n.secondary.href} className="!font-normal !text-muted hover:!text-accent" testId={`news-card-${n.id}-secondary-link`}>
            {n.secondary.label}
          </ExtLink>
        )}
      </div>
    </div>
  ) : (
    <span className="inline-flex rounded-none border border-line px-3 py-1 text-[12px] font-medium text-ink" data-testid={`news-card-${n.id}-spc-update`}>
      SPC update
    </span>
  );

// News item. `row` for the /news list, `card` for the home page strip.
export const NewsCard = forwardRef(({ n, variant = "row" }, ref) => {
  const motionProps = {
    ref,
    layout: true,
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0 },
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
    "data-testid": `news-card-${n.id}`,
  };

  if (variant === "card") {
    return (
      <motion.article {...motionProps} className="flex h-full flex-col border border-line bg-surface p-6 sm:p-8">
        <Meta n={n} />
        <h3 className="mt-6 font-display font-semibold tracking-[-0.025em] text-[1.5rem] leading-[1.18] text-ink">{n.headline}</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-muted">{n.summary}</p>
        <div className="mt-auto pt-7">
          <Source n={n} />
        </div>
      </motion.article>
    );
  }

  return (
    <motion.article {...motionProps} className="grid gap-5 border-t border-line py-8 md:grid-cols-12 md:gap-8 md:py-10">
      <div className="md:col-span-3">
        <Meta n={n} />
      </div>
      <div className="md:col-span-6">
        <h3 className="font-display font-semibold tracking-[-0.025em] text-2xl leading-[1.18] text-ink sm:text-[1.75rem]">{n.headline}</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-muted">{n.summary}</p>
      </div>
      <div className="md:col-span-3">
        <Source n={n} />
      </div>
    </motion.article>
  );
});
NewsCard.displayName = "NewsCard";
